import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import PinInput from './pin-input';

type PinFormProps = {
  pinLength?: number;
  createLabel?: string;
  confirmLabel?: string;
  submitLabel: string;
  loading?: boolean;
  errorText?: string | null;
  warning?: string;
  //  APIT-656: reset only — ask for the account password before the new PIN is accepted.
  requirePassword?: boolean;
  passwordLabel?: string;
  passwordError?: string | null;
  //  Set while the account is locked out; retrying cannot help until the lockout expires.
  submitDisabled?: boolean;
  onSubmit: (pin: string, password?: string) => void;
  onCancel?: () => void;
  cancelLabel?: string;
};

//  THIN-7681: create + confirm a 4-digit PIN. Shared by profile setup/reset and the
//  first-time setup wizard.
//
//  APIT-656: the reset path additionally gates on the account password (`requirePassword`).
//  Setup does not — /signing-pin/setup takes no password header, and a first-time PIN is not
//  replacing an existing signing credential. The password is verified server-side by
//  CurrentPasswordVerifier, which no-ops for accounts with no Keycloak-local password; there is
//  no status field telling the frontend which accounts those are, so the field is always shown.
export default function PinForm({
  pinLength = 4,
  createLabel = 'New 4-digit PIN',
  confirmLabel = 'Confirm new PIN',
  submitLabel,
  loading,
  errorText,
  warning,
  requirePassword = false,
  passwordLabel = 'Current account password',
  passwordError,
  submitDisabled = false,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
}: PinFormProps) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  //  Clear the password (and un-reveal it) whenever the backend rejects one. Each failed submit
  //  burns one of the five attempts before a 15-minute lockout, so a deliberate retype beats
  //  re-submitting the same wrong value, and a rejected credential should not sit on screen.
  useEffect(() => {
    if (passwordError) {
      setPassword('');
      setShowPassword(false);
    }
  }, [passwordError]);

  const complete = pin.length === pinLength && confirm.length === pinLength;
  const mismatch = touched && complete && pin !== confirm;
  const passwordReady = !requirePassword || password.length > 0;
  const canSubmit = complete && pin === confirm && passwordReady && !loading && !submitDisabled;

  const submit = () => {
    setTouched(true);
    if (pin.length === pinLength && pin === confirm && passwordReady) {
      onSubmit(pin, requirePassword ? password : undefined);
    }
  };

  return (
    <Stack spacing={2.5}>
      {warning && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          {warning}
        </Alert>
      )}
      {requirePassword && (
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 1 }}
          >
            {passwordLabel}
          </Typography>
          {/*
            `hiddenLabel` is required, not cosmetic: the theme defaults every TextField to the
            `filled` variant, which reserves paddingTop 21 / paddingBottom 4 (size small) for a
            floating label. This field labels itself with the Typography above instead of the
            `label` prop, so without `hiddenLabel` that reserved space stays empty and the typed
            text sits against the bottom of the box. `hiddenLabel` restores symmetric padding.
          */}
          <TextField
            fullWidth
            autoFocus
            hiddenLabel
            size="small"
            type={showPassword ? 'text' : 'password'}
            name="accountPassword"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading || submitDisabled}
            error={!!passwordError}
            helperText={passwordError || 'Confirms it’s you before your signing PIN changes.'}
            inputProps={{ 'aria-label': passwordLabel }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    size="small"
                    disabled={loading || submitDisabled}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {createLabel}
        </Typography>
        <PinInput value={pin} onChange={setPin} length={pinLength} autoFocus={!requirePassword} />
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {confirmLabel}
        </Typography>
        <PinInput value={confirm} onChange={setConfirm} length={pinLength} error={mismatch} />
      </Box>
      {mismatch && (
        <Typography variant="caption" color="error">
          PINs don&rsquo;t match.
        </Typography>
      )}
      {errorText && (
        <Typography variant="caption" color="error">
          {errorText}
        </Typography>
      )}
      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        {onCancel && (
          <Button onClick={onCancel} color="inherit" disabled={loading}>
            {cancelLabel}
          </Button>
        )}
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </Stack>
    </Stack>
  );
}
